import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { compressImage, formatCompressionStats } from '../utils/imageCompression';
import { DiscountClaimsCard } from './DiscountClaimsCard';
import {
  Store, Plus, Loader2, AlertTriangle, CheckCircle,
} from 'lucide-react';
import { ListingChecklist } from './merchant-dashboard/ListingChecklist';
import { MemberOfferCard } from './merchant-dashboard/MemberOfferCard';
import { BusinessLogoCard } from './merchant-dashboard/BusinessLogoCard';
import { PendingOrRejectedView } from './merchant-dashboard/PendingOrRejectedView';
import { MerchantStatsCards } from './merchant-dashboard/MerchantStatsCards';
import { ServicesList } from './merchant-dashboard/ServicesList';
import { ServiceFormDialog } from './merchant-dashboard/ServiceFormDialog';
import { ActivateMemberBenefitsBanner } from './merchant-dashboard/ActivateMemberBenefitsBanner';

const BUSINESS_TYPES = {
  food: 'Food & Beverage', retail: 'Retail & Shopping', health: 'Health & Wellness',
  beauty: 'Beauty & Salon', tech: 'Technology & IT', travel: 'Travel & Tourism',
  education: 'Education & Training', entertainment: 'Entertainment', automotive: 'Automotive',
  real_estate: 'Real Estate', professional: 'Professional Services', other: 'Other',
};

const emptyForm = {
  name: '', description: '', item_type: 'service', category_id: '', category: '',
  regular_price: '', tier_prices: {}, image_url: '', tags: '',
};

export function MerchantDashboard() {
  const { api, user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const limit = 10;

  // --- Data fetchers -----------------------------------------------------
  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await api.get('/api/merchant/dashboard');
      setDashboard(data);
    } catch (e) { /* non-blocking */ }
  }, [api]);

  const fetchServices = useCallback(async () => {
    setServicesLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      const { data } = await api.get(`/api/merchant/services?${params}`);
      setServices(data.services || []);
      setTotal(data.total || 0);
    } catch (e) { /* non-blocking */ }
    setServicesLoading(false);
  }, [api, page, search]);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await api.get('/api/merchant/subscription-plans');
      setPlans(data.plans || []);
    } catch (e) { /* non-blocking */ }
  }, [api]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchDashboard();
      setLoading(false);
    };
    init();
  }, [fetchDashboard]);

  useEffect(() => {
    if (dashboard?.merchant_status === 'approved') {
      fetchServices();
      fetchPlans();
    }
  }, [dashboard?.merchant_status, fetchServices, fetchPlans]);

  // --- Dialog handlers ---------------------------------------------------
  const openCreate = () => {
    setEditingService(null);
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const openEdit = (svc) => {
    setEditingService(svc);
    setForm({
      name: svc.name || '',
      description: svc.description || '',
      item_type: svc.item_type || 'service',
      category_id: svc.category_id || '',
      category: svc.category || '',
      regular_price: svc.regular_price?.toString() || '',
      tier_prices: svc.tier_prices || {},
      image_url: svc.image_url || '',
      tags: (svc.tags || []).join(', '),
    });
    setImageFile(null);
    setImagePreview(svc.image_url || null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Service name is required'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.regular_price || parseFloat(form.regular_price) <= 0) { toast.error('Valid price is required'); return; }

    setSaving(true);
    try {
      let imageUrl = form.image_url || null;
      if (imageFile) {
        const before = imageFile.size;
        const compressed = await compressImage(imageFile, { maxSide: 1600, quality: 0.82 });
        if (compressed.size < before) {
          toast.message(`Image compressed: ${formatCompressionStats(before, compressed.size)}`);
        }
        const fd = new FormData();
        fd.append('file', compressed);
        const isProduct = (form.item_type || 'service') === 'product';
        fd.append('purpose', isProduct ? 'product-photo' : 'service-photo');
        if (form.name?.trim()) fd.append('subject_name', form.name.trim());
        const uploadRes = await api.post('/api/upload/member-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        imageUrl = uploadRes.data.url || uploadRes.data.file_url || imageUrl;
      }

      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        item_type: form.item_type || 'service',
        category_id: form.category_id || null,
        category: form.category || 'Other',
        regular_price: parseFloat(form.regular_price),
        tier_prices: form.tier_prices || {},
        image_url: imageUrl,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };

      if (editingService) {
        await api.put(`/api/merchant/services/${editingService.id}`, payload);
        toast.success('Service updated');
      } else {
        await api.post('/api/merchant/services', payload);
        toast.success('Service created');
      }
      setDialogOpen(false);
      fetchServices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/merchant/services/${id}`);
      toast.success('Service deleted');
      setDeleteConfirm(null);
      fetchServices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (svc) => {
    try {
      await api.put(`/api/merchant/services/${svc.id}`, { is_active: !svc.is_active });
      toast.success(svc.is_active ? 'Service deactivated' : 'Service activated');
      fetchServices();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const totalPages = Math.ceil(total / limit);

  // --- Render ------------------------------------------------------------
  if (loading) {
    return (
      <MemberSidebarLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </MemberSidebarLayout>
    );
  }

  const status = dashboard?.merchant_status || 'pending';
  const info = dashboard?.merchant_info || {};
  const stats = dashboard?.stats || {};

  if (status !== 'approved') {
    return <PendingOrRejectedView dashboard={dashboard} status={status} info={info} onResubmitted={fetchDashboard} api={api} />;
  }

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="merchant-dashboard">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white flex items-center gap-2">
              <Store className="w-6 h-6 text-[#00E676]" /> {info.business_name || 'Merchant Dashboard'}
            </h1>
            <p className="text-[#A0B4CC] text-sm mt-1">
              <span className="inline-flex items-center gap-1 text-[#00E676] text-xs font-medium"><CheckCircle className="w-3 h-3" /> Approved</span>
              <span className="mx-2 text-white/20">|</span>
              {BUSINESS_TYPES[info.business_type] || info.business_type || 'Business'}
            </p>
          </div>
          <Button onClick={openCreate} className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90" data-testid="add-service-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Service
          </Button>
        </div>

        <ListingChecklist listing={dashboard?.marketplace_listing} />

        <MemberOfferCard api={api} info={dashboard?.merchant_info} onSaved={fetchDashboard} />

        <BusinessLogoCard
          api={api}
          currentUrl={dashboard?.merchant_info?.business_logo_url || ''}
          onUpdated={fetchDashboard}
        />

        <ActivateMemberBenefitsBanner user={user} api={api} />

        <MerchantStatsCards stats={stats} />

        <ServicesList
          services={services}
          servicesLoading={servicesLoading}
          search={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onToggleActive={handleToggleActive}
          onEdit={openEdit}
          onDelete={(id) => setDeleteConfirm(id)}
        />

        <ServiceFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editingService={editingService}
          form={form}
          setForm={setForm}
          imagePreview={imagePreview}
          setImagePreview={setImagePreview}
          setImageFile={setImageFile}
          plans={plans}
          saving={saving}
          onSave={handleSave}
          api={api}
        />

        <div className="mt-6">
          <DiscountClaimsCard mode="merchant" />
        </div>

        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#FF1744]" /> Delete Service
              </DialogTitle>
            </DialogHeader>
            <p className="text-[#A0B4CC] text-sm">Are you sure you want to delete this service? This action cannot be undone.</p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="text-[#A0B4CC]">Cancel</Button>
              <Button onClick={() => handleDelete(deleteConfirm)} className="bg-[#FF1744] text-white hover:bg-[#FF1744]/90" data-testid="confirm-delete-btn">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MemberSidebarLayout>
  );
}

export default MerchantDashboard;
